import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../store/slices/authSlice';
import { Result, Button, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';

/**
 * ProtectedRoute component - Protects routes based on user role/permissions
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string[]} props.allowedRoles - Array of allowed roles (e.g., ['admin', 'verifier'])
 * @param {boolean} props.requireAuth - If true, requires authentication (default: true)
 */
const ProtectedRoute = ({ children, allowedRoles = [], requireAuth = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [loading, setLoading] = React.useState(true);

  // Check if this is an OAuth callback (has token in URL)
  const urlParams = new URLSearchParams(window.location.search);
  const hasOAuthParams = urlParams.has('token') && urlParams.has('email');

  // Initialize user from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      // Always check localStorage for token and user, regardless of Redux state
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');

      if (storedUser && token) {
        try {
          const userData = JSON.parse(storedUser);

          // Only dispatch if Redux state is not already set
          if (!user || !isAuthenticated) {
            dispatch(setUser({ user: userData, token, isAuthenticated: true }));
          }
        } catch (error) {
          console.error('[ProtectedRoute] Failed to parse stored user:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, [dispatch, user, isAuthenticated]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  // Check if authentication is required
  // Also check localStorage directly as a fallback
  const hasToken = localStorage.getItem('token') || localStorage.getItem('authToken');

  // If this is an OAuth callback, allow through to let OAuthCallback process it
  if (hasOAuthParams) {
    return children;
  }

  if (requireAuth && !isAuthenticated && !hasToken) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  if (allowedRoles.length > 0) {
    // Get user from Redux or fallback to localStorage
    let currentUser = user;
    if (!currentUser) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          currentUser = JSON.parse(storedUser);
        } catch (error) {
          console.error('[ProtectedRoute] Failed to parse user from localStorage:', error);
        }
      }
    }

    // Get user role (check both role and is_admin for backward compatibility)
    const userRole = currentUser?.role || (currentUser?.is_admin ? 'admin' : 'user');

    // Check if user role is in allowed roles
    const hasAccess = allowedRoles.includes(userRole);

    if (!hasAccess) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: '20px'
        }}>
          <Result
            status="403"
            icon={<LockOutlined style={{ color: '#ff4d4f' }} />}
            title="403 - Access Denied"
            subTitle={
              <div>
                <p>Sorry, you don't have permission to access this page.</p>
                <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                  Required role: <strong>{allowedRoles.join(', ')}</strong>
                </p>
                <p style={{ fontSize: '14px', color: '#666' }}>
                  Your role: <strong>{userRole}</strong>
                </p>
              </div>
            }
            extra={[
              <Button type="primary" key="home" onClick={() => navigate('/')}>
                Go to Leaderboard
              </Button>,
              <Button key="login" onClick={() => navigate('/login')}>
                Login with Different Account
              </Button>,
            ]}
          />
        </div>
      );
    }
  }

  // User is authenticated and authorized, render children
  return children;
};

export default ProtectedRoute;
